import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../../api/client";
import { Button, Icon, Select, Spinner, cx,
  Modal,
  ModalOverlay
} from "../../components/ui";
import { getTodayISOInJakarta } from "../../utils/date";
import { useKunciPayroll } from "../salary/KunciPayroll";

const PERIOD_TABS = ["All Sessions", "Morning", "Afternoon", "Evening"];

const SESSION_PERIOD_COLOR = {
  Morning: "from-amber-400 to-orange-500",
  Afternoon: "from-sky-400 to-blue-600",
  Evening: "from-violet-500 to-purple-700",
  Other: "from-slate-400 to-slate-600"
};

export default function DashboardAdmin({ setToast, title = "Admin Dashboard" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("All Sessions");
  const [coverModal, setCoverModal] = useState(null);
  const [confirmingSchedule, setConfirmingSchedule] = useState(false);
  const [confirmScheduleModalOpen, setConfirmScheduleModalOpen] = useState(false);
  // Riwayat reset absensi untuk tanggal yang sedang dilihat, dan kartu yang
  // sedang dibuka dialog resetnya.
  const [resets, setResets] = useState([]);
  const [resetModal, setResetModal] = useState(null);

  const today = getTodayISOInJakarta();
  const [selectedDate, setSelectedDate] = useState(today);
  const [slideDir, setSlideDir] = useState("right");
  const selectedDateRef = useRef(selectedDate);
  const isViewingToday = selectedDate === today;
  // Pengganti boleh diatur untuk hari ini dan tanggal LAMPAU mana pun, selama payroll
  // bulannya belum dikunci. Dulu dibatasi ke bulan berjalan, jadi pengganti yang salah
  // di bulan lalu mustahil dibetulkan walau kelasnya belum diabsen. Kelas yang sudah
  // diabsen tetap ditolak server (dan kartunya sudah menyembunyikan tombolnya).
  const [tahunDilihat, bulanDilihat] = selectedDate.split("-").map(Number);
  const { kunci } = useKunciPayroll(tahunDilihat, bulanDilihat);
  const bulanTerkunci = !!kunci?.terkunci;
  const canEditDate = selectedDate <= today && !bulanTerkunci;

  async function load(d = selectedDate) {
    setLoading(true);
    try {
      const res = await api.getDashboardSchedules(d);
      if (res.success) setData(res);
      else setToast({ type: "error", message: res.message });
    } catch (err) {
      setToast({ type: "error", message: err.message });
    } finally {
      setLoading(false);
    }
    muatResets(d);
  }

  // Riwayat reset dimuat TERPISAH dan kegagalannya ditelan: di mode "Semua
  // Cabang" endpoint-nya menolak (reset berlaku per cabang), dan itu tak boleh
  // membuat dasbor jadwalnya ikut gagal tampil.
  async function muatResets(d = selectedDate) {
    try {
      const r = await api.listAttendanceResets(d);
      setResets(r?.success ? r.data || [] : []);
    } catch {
      setResets([]);
    }
  }

  async function batalkanReset(r) {
    const ok = window.confirm(
      `Batalkan reset "${r.kelas || r.session}" tanggal ${r.tanggal}?\n\n` +
        `Semua absensi yang tadi dihapus akan dikembalikan, dan gaji dihitung ulang.`
    );
    if (!ok) return;
    try {
      const res = await api.undoAttendanceReset(r.id);
      setToast({ type: res?.success ? "success" : "error", message: res?.message || "Selesai." });
      if (res?.success) await load();
    } catch (err) {
      setToast({ type: "error", message: err.message });
    }
  }

  useEffect(() => {
    selectedDateRef.current = selectedDate;
    load(selectedDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  useEffect(() => {
    // Auto-refresh tiap menit HANYA saat melihat hari ini
    const intervalId = window.setInterval(() => {
      if (selectedDateRef.current === getTodayISOInJakarta()) {
        load(selectedDateRef.current);
      }
    }, 60 * 1000);
    return () => window.clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function goToDate(iso) {
    if (!iso || iso > today || iso === selectedDate) return;
    setSlideDir(iso > selectedDate ? "right" : "left"); // maju→dari kanan, mundur→dari kiri
    setSelectedDate(iso);
  }

  function shiftDate(days) {
    const [y, m, d] = selectedDate.split("-").map(Number);
    const dt = new Date(y, m - 1, d + days);
    const iso = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
    goToDate(iso);
  }

  // ── Derived banner state — always reactive to latest data ──
  const bannerState = useMemo(() => {
    if (!data || !Array.isArray(data.schedules)) return null;
    const schedules = data.schedules;
    const coverageCards = schedules.filter((s) => s.coverage !== null);
    if (coverageCards.length === 0) return { type: "no_coverage" };
    const allLocked = coverageCards.every((s) => s.coverage?.isLocked);
    const anyLocked = coverageCards.some((s) => s.coverage?.isLocked);
    if (allLocked) return { type: "all_locked", count: coverageCards.length };
    if (anyLocked) return { type: "partial_locked", lockedCount: coverageCards.filter((s) => s.coverage?.isLocked).length, totalCount: coverageCards.length };
    return { type: "has_unlocked", count: coverageCards.length };
  }, [data]);

  const visibleSchedules = !data
    ? []
    : activeTab === "All Sessions"
      ? data.schedules
      : data.schedules.filter((s) => s.period === activeTab);

  if (loading) return <Spinner label="Loading dashboard…" />;

  const { summary, dayName, allTrainers, otherTrainers } = data || {};

  function todayISO_GMT7() {
    return getTodayISOInJakarta();
  }

  async function confirmScheduleToday() {
    setConfirmingSchedule(true);
    try {
      const res = await api.assignScheduleToday({ todayISO_GMT7: todayISO_GMT7() });
      if (res?.success) {
        // Draf yang kelasnya keburu diabsen trainer asli tak diaktifkan — tampil sebagai
        // galat supaya admin tak mengira semua penggantinya sudah masuk.
        setToast({
          type: res.rejected?.length ? "error" : "success",
          message: res.message || "Jadwal hari ini telah dikonfirmasi."
        });
        await load();
      } else {
        setToast({ type: "error", message: res?.message || "Gagal mengkonfirmasi jadwal." });
      }
    } catch (err) {
      setToast({ type: "error", message: err.message });
    } finally {
      setConfirmingSchedule(false);
    }
  }

  function handleConfirmScheduleToday() {
    setConfirmScheduleModalOpen(true);
  }

  // Batalkan coverage (mis. admin salah input) — kelas kembali ke trainer asli.
  async function cancelCoverage(sched) {
    const cleanSession = sched.session.split("|")[0];
    const cover = sched.coverage?.coverTrainer || "";
    const ok = window.confirm(
      `Batalkan coverage untuk "${cleanSession}"${cover ? ` (pengganti: ${cover})` : ""}?\n\n` +
        `Kelas akan kembali ke trainer asli (${sched.trainer || "-"}).`
    );
    if (!ok) return;
    try {
      const res = await api.cancelCoverage({
        session: cleanSession,
        date: selectedDate,
        originalTrainer: sched.trainer
      });
      setToast({ type: res.success ? "success" : "error", message: res.message });
      if (res.success) await load();
    } catch (err) {
      setToast({ type: "error", message: err.message });
    }
  }

  return (
    <div className="space-y-8">

      {/* ── Confirmation Banner — reactive to coverage lock state ── */}
      {bannerState && <ConfirmationBanner state={bannerState} />}

      {/* ── Header ── */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-sm font-bold uppercase tracking-widest text-primary">
            {dayName} · {selectedDate}
          </p>
          <h1 className="mt-1 text-4xl font-black tracking-tight">{title}</h1>
          <p className="mt-1 text-slate-500 text-sm">
            {isViewingToday
              ? "Live class schedule management & trainer coverage control."
              : bulanTerkunci
              ? "Payroll bulan ini sudah dikunci — jadwal penggantinya hanya-baca."
              : "Tanggal lampau — pengganti masih bisa diatur selama kelasnya belum diabsen."}
          </p>
        </div>

        <div className="flex flex-col items-end gap-2">
          {/* Navigasi tanggal */}
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => shiftDate(-1)}
              title="Hari sebelumnya"
              className="rounded-lg border border-slate-200 p-2 text-slate-600 hover:border-primary hover:text-primary dark:border-slate-700 dark:text-slate-300"
            >
              <Icon name="chevron_left" />
            </button>
            <input
              type="date"
              value={selectedDate}
              max={today}
              onChange={(e) => goToDate(e.target.value)}
              className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary dark:border-slate-700 dark:bg-slate-800 dark:text-white"
            />
            <button
              onClick={() => shiftDate(1)}
              disabled={isViewingToday}
              title="Hari berikutnya"
              className="rounded-lg border border-slate-200 p-2 text-slate-600 hover:border-primary hover:text-primary disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-700 dark:text-slate-300"
            >
              <Icon name="chevron_right" />
            </button>
            {!isViewingToday && (
              <button
                onClick={() => goToDate(today)}
                className="rounded-lg border border-primary px-3 py-2 text-sm font-bold text-primary hover:bg-primary/10"
              >
                Hari Ini
              </button>
            )}
          </div>

          {/* Aksi */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => load()}
              className="flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2 text-sm font-bold text-slate-600 hover:border-primary hover:text-primary dark:border-slate-700 dark:text-slate-300 transition"
            >
              <Icon name="refresh" />
              Refresh
            </button>

            {isViewingToday && (
              <Button
                variant="secondary"
                onClick={handleConfirmScheduleToday}
                disabled={confirmingSchedule || loading}
              >
                <Icon name={bannerState?.type === "all_locked" ? "edit_calendar" : "schedule"} />
                {confirmingSchedule
                  ? "Mengkonfirmasi…"
                  : bannerState?.type === "all_locked"
                  ? "Jadwal Terkonfirmasi · Edit"
                  : "Konfirmasi Jadwal Harian"}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* ── Konten harian (beranimasi geser saat pindah tanggal) ── */}
      <div
        key={selectedDate}
        className={cx(
          "space-y-8",
          slideDir === "right" ? "dash-slide-right" : "dash-slide-left"
        )}
      >
      {/* ── Summary Analytics Row ── */}
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard
          icon="calendar_today"
          label="Total Classes Today"
          value={summary?.totalClasses ?? 0}
          color="bg-blue-500"
        />
        <MetricCard
          icon="task_alt"
          label="Completed Classes"
          value={`${summary?.completedClasses ?? 0} / ${summary?.totalClasses ?? 0}`}
          sub="Selesai"
          color="bg-green-500"
        />
        <MetricCard
          icon="group"
          label="Active Students Today"
          value={summary?.totalStudents ?? 0}
          color="bg-amber-500"
        />
        <MetricCard
          icon="person_pin"
          label="Active Trainers Today"
          value={summary?.totalTrainers ?? 0}
          color="bg-purple-500"
        />
      </div>

      {/* ── Period Filter Tabs ── */}
      <div className="flex flex-wrap gap-2">
        {PERIOD_TABS.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={cx(
              "rounded-full px-5 py-2 text-sm font-bold transition",
              activeTab === tab
                ? "bg-primary text-white shadow-lg shadow-primary/30"
                : "border border-slate-200 bg-white text-slate-600 hover:border-primary hover:text-primary dark:border-slate-700 dark:bg-slate-900/50 dark:text-slate-300"
            )}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* ── Schedule Cards Grid ── */}
      {visibleSchedules.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 p-16 text-center text-slate-500 dark:border-slate-700">
          <Icon name="event_busy" className="text-5xl mb-3 block text-slate-300" />
          No classes found for this filter.
        </div>
      ) : (
        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
          {visibleSchedules.map((sched) => (
            <ScheduleCard
              key={sched.session}
              sched={sched}
              readOnly={!canEditDate}
              onAssignCover={() => setCoverModal({ sched })}
              onCancelCover={() => cancelCoverage(sched)}
              resetAktif={resetAktifUntuk(sched, resets)}
              onReset={() => setResetModal({ sched })}
              onUndoReset={batalkanReset}
            />
          ))}
        </div>
      )}

      {/* Riwayat reset tanggal ini. BUKAN pelengkap: kartu untuk hari di luar
          jadwal kelas hanya muncul karena ada absennya, jadi begitu absen di hari
          yang salah direset, kartunya ikut LENYAP — dan tombol Batalkan di kartu
          itu tak bisa dijangkau lagi. Kasus paling umum justru hanya bisa
          dibatalkan dari sini. */}
      <RiwayatReset resets={resets} onUndo={batalkanReset} />
      </div>

      {/* ── Coverage Modal ── */}
      {coverModal?.sched ? (
        <CoverageModal
          sched={coverModal.sched}
          allTrainers={allTrainers || []}
          otherTrainers={otherTrainers || []}
          onClose={() => setCoverModal(null)}
          assignDate={selectedDate}
          isBackdated={selectedDate < today}
          onSaved={() => {
            setCoverModal(null);
            load();
          }}
          setToast={setToast}
        />
      ) : null}

      {resetModal?.sched ? (
        <ResetAbsensiModal
          sched={resetModal.sched}
          date={selectedDate}
          setToast={setToast}
          onClose={() => setResetModal(null)}
          onDone={() => {
            setResetModal(null);
            load();
          }}
        />
      ) : null}

      {confirmScheduleModalOpen ? (
        <ConfirmScheduleModal
          pendingCoverageCount={data?.schedules?.filter((s) => s.coverage && !s.coverage.isLocked).length || 0}
          activeCoverageCount={data?.schedules?.filter((s) => s.coverage && s.coverage.isLocked).length || 0}
          onClose={() => setConfirmScheduleModalOpen(false)}
          onConfirm={async () => {
            setConfirmScheduleModalOpen(false);
            await confirmScheduleToday();
          }}
          confirming={confirmingSchedule}
        />
      ) : null}
    </div>
  );
}

// ─── ConfirmationBanner ───────────────────────────────────────────────────────
function ConfirmationBanner({ state }) {
  if (state.type === "no_coverage") {
    return (
      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800/40">
        <div className="flex items-center gap-3 text-sm text-slate-500 dark:text-slate-400">
          <Icon name="info" className="text-slate-400 text-xl shrink-0" />
          <span className="font-semibold">Tidak ada coverage hari ini. Semua kelas diajar oleh trainer asli.</span>
        </div>
      </div>
    );
  }

  if (state.type === "has_unlocked") {
    return (
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 shadow-sm dark:border-amber-800/50 dark:bg-amber-900/20">
        <div className="flex items-start gap-3">
          <Icon name="warning" className="text-amber-500 text-xl shrink-0 mt-0.5" />
          <div>
            <p className="font-black text-amber-800 dark:text-amber-200">
              {state.count} penggantian trainer menunggu Konfirmasi Jadwal Harian.
            </p>
            <p className="mt-0.5 text-xs text-amber-700 dark:text-amber-300">
              Simpan atau konfirmasi penggantian trainer di kartu kelas, lalu tekan <strong>Konfirmasi Jadwal Harian</strong> agar kelas masuk ke covered class trainer pengganti.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (state.type === "partial_locked") {
    return (
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 shadow-sm dark:border-amber-800/50 dark:bg-amber-900/20">
        <div className="flex items-start gap-3">
          <Icon name="lock_open" className="text-amber-500 text-xl shrink-0 mt-0.5" />
          <div>
            <p className="font-black text-amber-800 dark:text-amber-200">
              {state.lockedCount} dari {state.totalCount} coverage sudah aktif.
            </p>
            <p className="mt-0.5 text-xs text-amber-700 dark:text-amber-300">
              Masih ada coverage yang belum aktif. Tekan <strong>Konfirmasi Jadwal Harian</strong> agar semua penggantian masuk ke covered class.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (state.type === "all_locked") {
    return (
      <div className="rounded-2xl border border-green-200 bg-green-50 p-4 shadow-sm dark:border-green-800/50 dark:bg-green-900/20">
        <div className="flex items-center gap-3">
          <Icon name="verified" className="text-green-500 text-xl shrink-0" />
          <div>
            <p className="font-black text-green-800 dark:text-green-200">
              Semua {state.count} coverage sudah aktif!
            </p>
            <p className="mt-0.5 text-xs text-green-700 dark:text-green-300">
              Trainer pengganti sudah bisa melihat dan mengisi presensi kelas yang di-cover pada halaman Attendance.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return null;
}

// ─── MetricCard ──────────────────────────────────────────────────────────────
function MetricCard({ icon, label, value, sub, color }) {
  return (
    <div className="relative overflow-hidden rounded-2xl bg-white shadow-lg border border-slate-100 dark:border-slate-800 dark:bg-slate-900/60 p-5">
      <div className={cx("absolute -right-4 -top-4 h-20 w-20 rounded-full opacity-10", color)} />
      <div className={cx("mb-3 inline-flex h-11 w-11 items-center justify-center rounded-xl text-white", color)}>
        <Icon name={icon} />
      </div>
      <p className="text-xs font-bold uppercase tracking-widest text-slate-500">{label}</p>
      <p className="mt-1 text-3xl font-black">{value}</p>
      {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
    </div>
  );
}

function ConfirmScheduleModal({
  pendingCoverageCount,
  activeCoverageCount,
  onClose,
  onConfirm,
  confirming
}) {
  return (
    <ModalOverlay>
      <div className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl dark:border-slate-700 dark:bg-slate-900 space-y-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-black">Konfirmasi Jadwal Harian</h2>
            <p className="mt-1 text-sm text-slate-500">
              Jadwal harian akan dikunci dan coverage yang menunggu aktivasi akan masuk ke covered class trainer pengganti.
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={confirming}
            className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 disabled:opacity-50 dark:hover:bg-slate-800"
          >
            <Icon name="close" />
          </button>
        </div>

        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-700/40 dark:bg-amber-900/20 dark:text-amber-200">
          <p>
            Coverage menunggu aktivasi: <strong>{pendingCoverageCount}</strong>
          </p>
          <p>
            Coverage yang sudah aktif: <strong>{activeCoverageCount}</strong>
          </p>
        </div>

        <div className="rounded-xl bg-slate-50 px-4 py-3 text-sm text-slate-600 dark:bg-slate-800 dark:text-slate-300">
          <p>Pastikan penugasan trainer pengganti sudah benar sebelum melanjutkan.</p>
          <p>Jika belum yakin, pilih <strong>Cek Lagi</strong> untuk meninjau kartu kelas terlebih dahulu.</p>
        </div>

        <div className="flex gap-3">
          <Button
            variant="secondary"
            className="flex-1"
            onClick={onClose}
            disabled={confirming}
          >
            Cek Lagi
          </Button>
          <Button
            className="flex-1"
            onClick={onConfirm}
            disabled={confirming}
          >
            <Icon name="verified" />
            {confirming ? "Mengkonfirmasi…" : "Yes, Konfirmasi"}
          </Button>
        </div>
      </div>
    </ModalOverlay>
  );
}

// ─── ScheduleCard ─────────────────────────────────────────────────────────────
function CoverageTypeBadge({ type }) {
  const merge = type === "merge";
  return (
    <span
      className={cx(
        "ml-1.5 rounded-full px-1.5 py-0.5 text-[9px] font-black uppercase align-middle",
        merge ? "bg-blue-500/15 text-blue-600" : "bg-slate-500/15 text-slate-600 dark:text-slate-300"
      )}
    >
      {merge ? "Gabung" : "Ganti"}
    </span>
  );
}

// Kalimat di kartu coverage aktif. Hari ini pengganti langsung bisa mengisi; hari LAMPAU
// hanya selama izin isinya masih berlaku (dibuka 24 jam saat coverage-nya disimpan, atau
// lewat "Minta Edit" yang disetujui).
export function ketIzinPengganti(cov, sekarang = Date.now() / 1000) {
  if (!cov || !cov.lampau) return "Trainer pengganti sudah bisa mengisi presensi kelas ini";
  if (cov.izinSampai && cov.izinSampai > sekarang) {
    const sampai = new Date(cov.izinSampai * 1000).toLocaleString("id-ID", {
      timeZone: "Asia/Jakarta", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit"
    });
    return `Pengganti bisa mengisi presensi sampai ${sampai} WIB`;
  }
  return "Izin isi pengganti sudah lewat — pengganti perlu \"Minta Edit\" dulu";
}

export function ScheduleCard({
  sched,
  onAssignCover,
  onCancelCover,
  readOnly,
  resetAktif = null,
  onReset,
  onUndoReset
}) {
  // session key may have "|TrainerName" suffix added by backend for UI uniqueness
  // strip it for display
  const sessionDisplay = sched.session.split("|")[0];
  const gradClass = SESSION_PERIOD_COLOR[sched.period] || SESSION_PERIOD_COLOR.Other;
  const isLocked = sched.coverage?.isLocked;
  const isDone = sched.status === "Selesai";
  // Satu absen pun sudah mengunci pilihan pengganti (backend menolak dengan aturan sama).
  const sudahDiabsen = (sched.completedCount || 0) > 0;

  return (
    <div className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-lg dark:border-slate-800 dark:bg-slate-900/50 flex flex-col">
      <div className={cx("h-1.5 w-full bg-gradient-to-r", gradClass)} />

      <div className="flex-1 p-5 space-y-4">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-slate-400">{sched.period}</p>
            <p className="text-xl font-black">{sessionDisplay}</p>
            {sched.mergedIn?.length > 0 && (
              <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-blue-500/15 px-2 py-0.5 text-[10px] font-black text-blue-600 dark:text-blue-300">
                <Icon name="merge_type" className="text-xs" />
                Kelas Gabungan · + {sched.mergedIn.join(", ")}
              </span>
            )}
            {/* Kelas ini muncul karena perkiraan (tiap hari kerja), bukan karena
                jadwalnya — harinya memang belum pernah diisi. Ditandai supaya admin
                tak mengira ini jadwal yang pasti. */}
            {sched.hariBelumDiisi && (
              <span
                title="Hari kelas ini belum diisi di Manajemen Kelas, jadi ia ditampilkan tiap hari kerja"
                className="mt-1 inline-flex items-center gap-1 rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-black text-amber-600 dark:text-amber-400"
              >
                <Icon name="edit_calendar" className="text-xs" />
                Hari belum diisi
              </span>
            )}
          </div>
          <StatusBadge status={sched.status} />
        </div>

        {/* Trainer Info */}
        {!sched.coverage ? (
          <div className="flex items-center gap-2 text-sm">
            <Icon name="person" className="text-slate-400 text-base" />
            <span className="font-bold text-slate-700 dark:text-slate-200">{sched.trainer || "—"}</span>
            {sched.mergedIn?.length > 0 && (
              <span className="text-xs font-bold text-blue-600 dark:text-blue-300">
                + kelas {sched.mergedIn.join(", ")}
              </span>
            )}
          </div>
        ) : !isLocked ? (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 dark:border-amber-500/20 dark:bg-amber-900/20">
            <div className="flex items-center gap-1 mb-1">
              <Icon name="swap_horiz" className="text-amber-500 text-sm" />
              <span className="text-xs font-black text-amber-600 dark:text-amber-400 uppercase tracking-wide">
                Coverage - Menunggu Aktivasi
              </span>
            </div>
            <p className="text-xs text-slate-600 dark:text-slate-300">
              <span className="font-bold">{sched.coverage.originalTrainer}</span>
              <span className="mx-1.5 text-slate-400">➔</span>
              <span className="font-black text-amber-600 dark:text-amber-300">{sched.coverage.coverTrainer}</span>
              <CoverageTypeBadge type={sched.coverage.coverageType} />
            </p>
            <p className="mt-1 text-[10px] text-amber-600 dark:text-amber-400">
              Menunggu tombol Konfirmasi Jadwal Harian agar masuk ke covered class
            </p>
          </div>
        ) : (
          <div className="rounded-xl border border-purple-200 bg-purple-50 px-3 py-2 dark:border-purple-500/20 dark:bg-purple-900/20">
            <div className="flex items-center gap-1 mb-1">
              <Icon name="lock" className="text-purple-500 text-sm" />
              <span className="text-xs font-black text-purple-600 dark:text-purple-400 uppercase tracking-wide">
                Covered &amp; Locked
              </span>
            </div>
            <p className="text-xs text-slate-600 dark:text-slate-300">
              <span className="font-bold">{sched.coverage.originalTrainer}</span>
              <span className="mx-1.5 text-slate-400">➔</span>
              <span className="font-black text-purple-600 dark:text-purple-300">{sched.coverage.coverTrainer}</span>
              <CoverageTypeBadge type={sched.coverage.coverageType} />
            </p>
            <p className="mt-1 text-[10px] text-purple-500 dark:text-purple-400">
              {ketIzinPengganti(sched.coverage)}
            </p>
          </div>
        )}

        {/* Progress bar */}
        <div>
          <div className="flex justify-between text-xs text-slate-500 mb-1">
            <span className="font-bold">
              {sched.completedCount}/{sched.totalStudents} students attended
            </span>
            <span>
              {sched.totalStudents > 0
                ? Math.round((sched.completedCount / sched.totalStudents) * 100)
                : 0}%
            </span>
          </div>
          <div className="h-2 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
            <div
              className={cx("h-full rounded-full transition-all", isDone ? "bg-green-500" : "bg-primary")}
              style={{
                width:
                  sched.totalStudents > 0
                    ? `${(sched.completedCount / sched.totalStudents) * 100}%`
                    : "0%"
              }}
            />
          </div>
        </div>
      </div>

      {/* Footer action */}
      {isDone ? (
        // Kelas sudah selesai → coverage terkunci (tak bisa diedit/dibatalkan).
        <div className="border-t border-slate-100 dark:border-slate-800 p-4">
          <div className="flex items-center justify-center gap-2 text-xs font-bold text-slate-400">
            <Icon name="lock" className="text-sm" />
            Kelas sudah selesai — coverage terkunci
          </div>
        </div>
      ) : readOnly ? null : (
        <div className="border-t border-slate-100 dark:border-slate-800 p-4 space-y-2">
          {isLocked ? (
            // Coverage aktif TAPI sesi belum selesai → masih boleh dibatalkan.
            <>
              <div className="flex items-center justify-center gap-2 text-xs font-bold text-purple-500">
                <Icon name="verified_user" className="text-sm" />
                Coverage Aktif — pengganti sudah masuk Attendance
              </div>
              <CancelCoverageButton onClick={onCancelCover} />
            </>
          ) : sched.coverage ? (
            // Coverage draft (belum dikonfirmasi) → Edit + Batalkan. Bila kelasnya keburu
            // diabsen trainer asli, draf tak akan diaktifkan — tinggal dibatalkan.
            <>
              {sudahDiabsen ? (
                <p className="text-center text-xs font-bold text-amber-600 dark:text-amber-400">
                  Kelas sudah mulai diabsen trainer asli — draf ini tak bisa diaktifkan.
                </p>
              ) : (
                <Button variant="secondary" className="w-full" onClick={onAssignCover}>
                  <Icon name="swap_horiz" />
                  Edit Penggantian Trainer
                </Button>
              )}
              <CancelCoverageButton onClick={onCancelCover} />
            </>
          ) : sudahDiabsen ? (
            <div className="flex items-center justify-center gap-2 text-xs font-bold text-slate-400">
              <Icon name="lock" className="text-sm" />
              Kelas sudah mulai diabsen — pengganti tak bisa diatur
            </div>
          ) : (
            // Belum ada coverage → Assign.
            <Button variant="secondary" className="w-full" onClick={onAssignCover}>
              <Icon name="swap_horiz" />
              Assign Cover Trainer
            </Button>
          )}
        </div>
      )}

      <BarisReset
        sched={sched}
        isDone={isDone}
        resetAktif={resetAktif}
        onReset={onReset}
        onUndoReset={onUndoReset}
      />
    </div>
  );
}

// ── Reset absensi di kartu ─────────────────────────────────────────────────
//
// Baris TERSENDIRI, di luar aturan footer di atasnya. Footer itu menyembunyikan
// semua tombol untuk kelas yang sudah Selesai dan untuk tanggal di luar bulan
// berjalan — padahal justru kartu-kartu itulah yang paling perlu direset.
// Reset mengikuti KUNCI PAYROLL (diperiksa server), bukan aturan bulan berjalan.
//
// Tautan kecil, bukan tombol utama: kartu ini diklik setiap hari untuk urusan lain,
// dan reset menghapus dasar gaji. Hanya muncul bila kartunya memang punya absensi.
export function resetAktifUntuk(sched, resets) {
  const ids = JSON.stringify((sched.classIds || []).slice().sort((a, b) => a - b));
  const sesi = String(sched.session || "").split("|")[0];
  return (
    (resets || []).find(
      (r) =>
        r.bisaDibatalkan &&
        r.session === sesi &&
        JSON.stringify((r.classIds || []).slice().sort((a, b) => a - b)) === ids
    ) || null
  );
}

function BarisReset({ sched, isDone, resetAktif, onReset, onUndoReset }) {
  const punyaKelas = (sched.classIds || []).length > 0;
  const adaAbsen = Number(sched.completedCount || 0) > 0 || isDone;

  // Baru direset & belum diabsen ulang → yang berguna adalah tombol batalkan,
  // plus penjelasan kenapa kartunya mendadak kosong.
  if (resetAktif && !adaAbsen) {
    return (
      <div className="flex items-center justify-between gap-2 border-t border-slate-100 bg-amber-500/5 px-4 py-2.5 text-xs dark:border-slate-800">
        <span className="min-w-0 truncate text-amber-700 dark:text-amber-400">
          <Icon name="restart_alt" className="mr-1 align-middle text-sm" />
          Direset{resetAktif.oleh ? ` oleh ${resetAktif.oleh}` : ""}
        </span>
        <button
          type="button"
          onClick={() => onUndoReset && onUndoReset(resetAktif)}
          className="shrink-0 font-black text-primary hover:underline"
        >
          Batalkan reset
        </button>
      </div>
    );
  }

  if (!punyaKelas || !adaAbsen || !onReset) return null;
  return (
    <div className="border-t border-slate-100 px-4 py-2 dark:border-slate-800">
      <button
        type="button"
        onClick={onReset}
        className="flex w-full items-center justify-center gap-1 rounded-lg py-1 text-xs font-bold text-red-500 transition hover:bg-red-500/10 hover:text-red-600"
      >
        <Icon name="restart_alt" className="text-sm" />
        Reset absensi kelas ini
      </button>
    </div>
  );
}

const LABEL_STATUS = { attend: "hadir", absent: "tidak hadir", izin: "izin", permission: "izin" };

function bacaSel(nilai) {
  const [dasar, pengisi] = String(nilai || "").split(":");
  const label = LABEL_STATUS[String(dasar || "").trim().toLowerCase()] || dasar;
  return pengisi ? `${label} (diisi ${pengisi.trim()})` : label;
}

// ── Dialog reset — pratinjau DULU, baru boleh menekan ──────────────────────────────────
export function ResetAbsensiModal({ sched, date, onClose, onDone, setToast }) {
  const [pratinjau, setPratinjau] = useState(null);
  const [memuat, setMemuat] = useState(true);
  const [alasan, setAlasan] = useState("");
  const [kirim, setKirim] = useState(false);
  const sesi = String(sched.session || "").split("|")[0];

  useEffect(() => {
    let batal = false;
    (async () => {
      try {
        const r = await api.previewAttendanceReset({ date, classIds: sched.classIds || [] });
        if (batal) return;
        if (r?.success) setPratinjau(r);
        else {
          setToast({ type: "error", message: r?.message || "Gagal memuat pratinjau." });
          onClose();
        }
      } catch (err) {
        if (batal) return;
        setToast({ type: "error", message: err.message || "Gagal memuat pratinjau." });
        onClose();
      } finally {
        if (!batal) setMemuat(false);
      }
    })();
    return () => {
      batal = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cukup = alasan.trim().length >= 5;
  const bisa = !!pratinjau && !pratinjau.terkunci && !pratinjau.kosong && cukup && !kirim;

  async function jalankan() {
    if (!bisa) return;
    setKirim(true);
    try {
      const r = await api.resetAttendance({
        date,
        classIds: sched.classIds || [],
        reason: alasan.trim()
      });
      setToast({ type: r?.success ? "success" : "error", message: r?.message || "Selesai." });
      if (r?.success) onDone();
      else setKirim(false);
    } catch (err) {
      setToast({ type: "error", message: err.message || "Gagal mereset." });
      setKirim(false);
    }
  }

  const h = pratinjau?.hitungan || {};
  const baris = [
    ["how_to_reg", h.kehadiran, "catatan kehadiran murid kelas"],
    ["person_add", h.muridTambahan, "murid tambahan (dari jadwal lain)"],
    ["task_alt", h.tandaSelesai, "tanda Selesai"],
    ["checklist", h.todo, "to-do list kelas"],
    ["schedule", h.jamDatang, "jam datang"]
  ].filter((b) => Number(b[1]) > 0);

  return (
    <Modal title="Reset Absensi" open onClose={onClose}>
      <div className="space-y-4 text-sm">
        <p className="text-slate-500">
          <span className="font-bold text-slate-700 dark:text-slate-200">{sesi}</span>
          {" · "}
          {sched.trainer || "—"}
          {" · "}
          {date}
        </p>

        {memuat ? (
          <Spinner label="Memeriksa apa saja yang akan terhapus…" />
        ) : !pratinjau ? null : pratinjau.terkunci ? (
          <div className="rounded-xl bg-red-500/10 p-3 font-bold text-red-600 dark:text-red-400">
            <Icon name="lock" className="mr-1 align-middle text-base" />
            Payroll bulan ini sudah dikunci. Reset tak bisa dilakukan sampai super admin
            membukanya.
          </div>
        ) : pratinjau.kosong ? (
          <div className="rounded-xl bg-slate-100 p-3 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
            Tak ada absensi di kartu ini untuk direset.
          </div>
        ) : (
          <>
            {(pratinjau.kelas || []).length > 1 && (
              <div className="rounded-xl bg-amber-500/10 p-3 text-xs font-bold text-amber-700 dark:text-amber-400">
                Kartu ini mewakili {pratinjau.kelas.length} kelas:{" "}
                {pratinjau.kelas.map((k) => k.name).join(", ")} — semuanya ikut direset.
              </div>
            )}

            <div>
              <p className="mb-2 text-xs font-black uppercase tracking-wider text-slate-500">
                Yang akan dihapus
              </p>
              <ul className="space-y-1.5">
                {baris.map(([ikon, n, teks]) => (
                  <li key={teks} className="flex items-center gap-2">
                    <Icon name={ikon} className="text-base text-red-500" />
                    <span className="font-black">{n}</span>
                    <span className="text-slate-600 dark:text-slate-300">{teks}</span>
                  </li>
                ))}
              </ul>
            </div>

            {(pratinjau.murid || []).length > 0 && (
              <div className="max-h-40 overflow-y-auto rounded-xl border border-slate-200 p-2 text-xs dark:border-slate-700">
                {pratinjau.murid.map((m) => (
                  <div key={m.studentId} className="flex justify-between gap-2 py-0.5">
                    <span className="truncate font-bold">{m.nama}</span>
                    <span className="shrink-0 text-slate-500">{bacaSel(m.nilai)}</span>
                  </div>
                ))}
                {(pratinjau.tambahan || []).map((a) => (
                  <div key={`t-${a.studentId}-${a.trainer}`} className="flex justify-between gap-2 py-0.5">
                    <span className="truncate font-bold">{a.nama}</span>
                    <span className="shrink-0 text-slate-500">tambahan · {a.status}</span>
                  </div>
                ))}
              </div>
            )}

            {(pratinjau.dibukaUntuk || []).length > 0 && (
              <div className="rounded-xl bg-primary/10 p-3 text-xs text-primary">
                <Icon name="lock_open" className="mr-1 align-middle text-sm" />
                Sesudah direset, hari ini dibuka <b>24 jam</b> supaya{" "}
                <b>{pratinjau.dibukaUntuk.join(", ")}</b> bisa mengabsen ulang.
              </div>
            )}

            <p className="text-xs text-slate-500">
              Gaji trainer bulan ini dihitung ulang otomatis. Reset bisa dibatalkan selama
              kelas ini belum diabsen ulang.
            </p>

            <label className="block">
              <span className="mb-1 block text-xs font-black uppercase tracking-wider text-slate-500">
                Alasan (wajib)
              </span>
              <textarea
                value={alasan}
                onChange={(e) => setAlasan(e.target.value)}
                rows={2}
                maxLength={500}
                placeholder="mis. trainer salah mengabsen kelas di hari yang salah"
                aria-label="Alasan reset"
                className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary dark:border-slate-700 dark:bg-slate-800 dark:text-white"
              />
              {!cukup && alasan.length > 0 && (
                <span className="text-[11px] text-amber-600">Minimal 5 huruf.</span>
              )}
            </label>
          </>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="secondary" onClick={onClose} disabled={kirim}>
            Batal
          </Button>
          {!!pratinjau && !pratinjau.terkunci && !pratinjau.kosong && (
            <Button variant="danger" onClick={jalankan} disabled={!bisa}>
              {kirim ? "Mereset…" : "Reset absensi"}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}

// ── Riwayat reset tanggal ini ──────────────────────────────────────────────────
function waktuSingkat(v) {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("id-ID", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

export function RiwayatReset({ resets, onUndo }) {
  if (!resets || !resets.length) return null;
  return (
    <div className="anim-fade rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900/50">
      <p className="mb-3 flex items-center gap-2 font-black">
        <Icon name="history" className="text-primary" />
        Riwayat reset absensi tanggal ini
      </p>
      <div className="divide-y divide-slate-100 dark:divide-slate-800">
        {resets.map((r) => (
          <div key={r.id} className="flex flex-wrap items-center justify-between gap-3 py-3 text-sm">
            <div className="min-w-0">
              <p className="font-bold">
                {r.kelas || r.session}
                {r.dibatalkanPada && (
                  <span className="ml-2 rounded-full bg-green-500/15 px-2 py-0.5 text-[10px] font-black text-green-600">
                    dibatalkan
                  </span>
                )}
              </p>
              <p className="text-xs text-slate-500">
                {r.ringkasan}
                {r.alasan ? ` — “${r.alasan}”` : ""}
              </p>
              <p className="text-[11px] text-slate-400">
                oleh {r.oleh || "—"} · {waktuSingkat(r.pada)}
                {r.dibatalkanPada &&
                  ` · dibatalkan oleh ${r.dibatalkanOleh || "—"} · ${waktuSingkat(r.dibatalkanPada)}`}
              </p>
            </div>
            {r.bisaDibatalkan && (
              <Button variant="secondary" onClick={() => onUndo(r)}>
                <Icon name="undo" className="text-base" />
                Batalkan reset
              </Button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function CancelCoverageButton({ onClick }) {
  return (
    <button
      onClick={onClick}
      className="flex w-full items-center justify-center gap-1.5 rounded-xl border border-red-200 px-4 py-2 text-sm font-black text-red-600 transition hover:bg-red-50 dark:border-red-800/50 dark:hover:bg-red-900/20"
    >
      <Icon name="cancel" className="text-base" />
      Batalkan Coverage
    </button>
  );
}

function StatusBadge({ status }) {
  const done = status === "Selesai";
  const notDone = status === "Tidak Selesai";
  const label = status || "Belum Mulai";
  const pill = done
    ? "bg-green-500/15 text-green-600"
    : notDone
    ? "bg-red-500/15 text-red-600"
    : "bg-amber-500/15 text-amber-600";
  const dot = done ? "bg-green-500" : notDone ? "bg-red-500" : "bg-amber-500";
  return (
    <span
      className={cx(
        "inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-black",
        pill
      )}
    >
      <span className={cx("h-1.5 w-1.5 rounded-full", dot)} />
      {label}
    </span>
  );
}

// ─── CoverageModal ────────────────────────────────────────────────────────────
function CoverageModal({ sched, allTrainers, otherTrainers = [], onClose, onSaved, setToast, assignDate, isBackdated }) {
  const [selected, setSelected] = useState("");
  const [coverageType, setCoverageType] = useState("substitution");
  const [saving, setSaving] = useState(false);
  const [confirmingReplacement, setConfirmingReplacement] = useState(false);
  const [isLocked, setIsLocked] = useState(false);

  // Strip "|TrainerName" suffix — this is a UI-only uniqueness key, NOT the real session name
  const cleanSession = sched.session.split("|")[0];
  const originalTrainer = sched.trainer || "";

  // Trainer terpilih berasal dari CABANG LAIN? → hanya boleh Pergantian (merge lintas-cabang dilarang).
  const selectedFromOther =
    !!selected && otherTrainers.some((t) => t.toLowerCase() === selected.toLowerCase());

  useEffect(() => {
    if (sched.coverage?.coverTrainer) {
      setSelected(sched.coverage.coverTrainer);
      setCoverageType(sched.coverage.coverageType || "substitution");
      setIsLocked(sched.coverage.isLocked || false);
    }
  }, [sched]);

  // Paksa "Pergantian" bila pengganti dari cabang lain (backend menolak merge lintas-cabang).
  useEffect(() => {
    if (selectedFromOther && coverageType === "merge") setCoverageType("substitution");
  }, [selectedFromOther, coverageType]);

  async function handleSave() {
    if (!selected) {
      setToast({ type: "error", message: "Pilih trainer pengganti terlebih dahulu." });
      return;
    }
    setSaving(true);
    try {
      const res = await api.assignCoverage({
        session: cleanSession,
        date: assignDate,
        originalTrainer,
        coverTrainer: selected,
        type: coverageType
      });
      if (res.success) {
        setToast({ type: "success", message: res.message });
        onSaved();
      } else {
        setToast({ type: "error", message: res.message });
      }
    } catch (err) {
      setToast({ type: "error", message: err.message });
    } finally {
      setSaving(false);
    }
  }

  async function handleConfirmReplacement() {
    if (!selected) {
      setToast({ type: "error", message: "Pilih trainer pengganti terlebih dahulu." });
      return;
    }
    setConfirmingReplacement(true);
    try {
      const res = await api.assignCoverage({
        session: cleanSession,
        date: assignDate,
        originalTrainer,
        coverTrainer: selected,
        type: coverageType
      });
      if (res.success) {
        setIsLocked(false);
        setToast({ type: "success", message: res.message });
        onSaved();
      } else {
        setToast({ type: "error", message: res.message });
      }
    } catch (err) {
      setToast({ type: "error", message: err.message });
    } finally {
      setConfirmingReplacement(false);
    }
  }

  return (
    <ModalOverlay>
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl dark:border-slate-700 dark:bg-slate-900 space-y-5">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-black">Assign Cover Trainer</h2>
            <p className="mt-0.5 text-xs text-slate-500">
              {cleanSession} · {assignDate}
            </p>
            {isBackdated && (
              <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-black text-amber-600">
                <Icon name="history" className="text-sm" />
                Tanggal lampau — langsung aktif; pengganti bisa langsung mengisi absensi 24 jam
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            <Icon name="close" />
          </button>
        </div>

        {/* Original Trainer */}
        <div className="rounded-xl bg-slate-50 dark:bg-slate-800 px-4 py-3">
          <p className="text-xs font-bold uppercase text-slate-400">Trainer Asli (Original)</p>
          <p className="font-black text-primary text-lg">{originalTrainer || "—"}</p>
        </div>

        {/* Trainer selector */}
        <div>
          <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500">
            Trainer Pengganti
          </label>
          <Select
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            disabled={isLocked}
          >
            <option value="">— Pilih trainer pengganti —</option>
            <optgroup label={coverageType === "merge" ? "Trainer di cabang ini (kelas digabung ke trainer ini)" : "Cabang ini"}>
              {allTrainers
                .filter((t) => t.toLowerCase() !== originalTrainer.toLowerCase())
                .map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
            </optgroup>
            {/* Penggabungan HARUS se-cabang → sembunyikan trainer cabang lain saat merge. */}
            {coverageType !== "merge" && otherTrainers.length > 0 && (
              <optgroup label="Cabang lain (pergantian lintas-cabang)">
                {otherTrainers
                  .filter((t) => t.toLowerCase() !== originalTrainer.toLowerCase())
                  .map((t) => (
                    <option key={"o-" + t} value={t}>{t}</option>
                  ))}
              </optgroup>
            )}
          </Select>
          {coverageType === "merge" ? (
            <p className="mt-1.5 text-[11px] font-bold text-slate-500">
              Penggabungan hanya dengan trainer <b>di cabang ini</b> — kelas ini dilebur ke kelasnya.
            </p>
          ) : selectedFromOther ? (
            <p className="mt-1.5 text-[11px] font-bold text-amber-600 dark:text-amber-400">
              Trainer dari cabang lain → hanya bisa sebagai <b>Pergantian</b> (penggabungan lintas-cabang tidak diizinkan).
            </p>
          ) : null}
        </div>

        {/* Jenis coverage: pergantian vs penggabungan */}
        <div>
          <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500">
            Jenis Coverage
          </label>
          <div className="grid grid-cols-2 gap-2">
            {[
              {
                key: "substitution",
                title: "Pergantian",
                desc: "Trainer pengganti mengajar kelas ini. Bila ia tetap mengajar kelasnya sendiri, headcount dijumlah; bila kelasnya diambil orang lain, kelas sendiri jadi lantai."
              },
              {
                key: "merge",
                title: "Penggabungan",
                desc: "Kelas ini dilebur ke kelas trainer pengganti. Headcount dijumlah → satu tier gaji."
              }
            ].map((opt) => (
              <button
                key={opt.key}
                type="button"
                disabled={isLocked || (opt.key === "merge" && selectedFromOther)}
                onClick={() => setCoverageType(opt.key)}
                title={opt.key === "merge" && selectedFromOther ? "Penggabungan tidak tersedia untuk trainer cabang lain" : undefined}
                className={cx(
                  "rounded-xl border p-3 text-left transition disabled:opacity-60",
                  coverageType === opt.key
                    ? "border-primary bg-primary/10 ring-1 ring-primary"
                    : "border-slate-200 hover:border-primary/50 dark:border-slate-700"
                )}
              >
                <p className="flex items-center gap-1 text-sm font-black">
                  <Icon
                    name={opt.key === "merge" ? "merge_type" : "swap_horiz"}
                    className="text-base"
                  />
                  {opt.title}
                </p>
                <p className="mt-0.5 text-[11px] leading-snug text-slate-500">{opt.desc}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Info box */}
        {!isLocked && (
          <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-xs text-blue-800 dark:border-blue-800/40 dark:bg-blue-900/20 dark:text-blue-300 space-y-1">
            <p className="font-black flex items-center gap-1">
              <Icon name="info" className="text-sm" /> Dua langkah
            </p>
            <p><b>1.</b> Pilih trainer pengganti di sini, lalu simpan.</p>
            <p><b>2.</b> Tekan <strong>Konfirmasi Jadwal Harian</strong> di dashboard.</p>
            <p className="pt-1 opacity-80">Setelah itu kelas masuk ke Attendance trainer pengganti, dan absensinya otomatis terhitung ke gajinya.</p>
          </div>
        )}

        {/* Actions */}
        {isLocked ? (
          <div className="flex items-center justify-center gap-2 rounded-xl border border-purple-200 bg-purple-50 py-4 text-sm font-black text-purple-600 dark:border-purple-500/20 dark:bg-purple-900/20 dark:text-purple-400">
            <Icon name="lock" />
            Coverage Aktif - Trainer pengganti sudah masuk Attendance
          </div>
        ) : (
          <div className="flex gap-3">
            <Button
              variant="secondary"
              className="flex-1"
              onClick={handleSave}
              disabled={saving || confirmingReplacement || !selected}
            >
              {saving ? "Menyimpan…" : "Simpan Dulu"}
            </Button>
            <Button
              className="flex-1"
              onClick={handleConfirmReplacement}
              disabled={saving || confirmingReplacement || !selected}
            >
              <Icon name="verified" />
              {confirmingReplacement ? "Mengkonfirmasi…" : "Konfirmasi Penggantian"}
            </Button>
          </div>
        )}
      </div>
    </ModalOverlay>
  );
}
